import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { createShareTelemetry, nativeShareOutcome } from './shareTelemetry';

// Bundled iOS pages have capacitor://localhost URLs, which other apps cannot
// open. Share the same route on the public site instead.
export function publicShareUrl(address = window.location.href) {
  const parsed = new URL(address, window.location.href);
  if (!Capacitor.isNativePlatform()) return parsed.href;
  if (parsed.protocol === 'capacitor:' || parsed.hostname === 'localhost') {
    return `https://wiw.today${parsed.pathname}${parsed.search}${parsed.hash}`;
  }
  return parsed.href;
}

export async function shareLink(data, context) {
  const telemetry = createShareTelemetry(context, data?.url);
  try {
    if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Share')) {
      // Invoke the OS UI before any analytics/network work while the original
      // gesture is active. Only the OS knows whether an app was selected.
      const pending = Share.share(data);
      telemetry.start();
      const result = await pending;
      const outcome = nativeShareOutcome(result);
      telemetry.finish(outcome);
      return outcome === 'copied' ? 'copied' : 'shared';
    } else if (typeof navigator.share === 'function' && (!navigator.canShare || navigator.canShare(data))) {
      // Keep the original tap's transient activation for Safari's share sheet.
      const pending = navigator.share(data);
      telemetry.start();
      await pending;
      // A fulfilled Web Share promise cannot prove recipient delivery.
      telemetry.finish('web_resolved');
    } else {
      if (typeof navigator.clipboard?.writeText !== 'function') {
        telemetry.finish('unsupported');
        throw Object.assign(new Error('Could not copy link'), { code: 'SHARE_COPY_FAILED' });
      }
      try {
        const pending = navigator.clipboard.writeText([data.text?.trim(), data.url].filter(Boolean).join('\n'));
        telemetry.start();
        await pending;
      } catch {
        throw Object.assign(new Error('Could not copy link'), { code: 'SHARE_COPY_FAILED' });
      }
      telemetry.finish('copied');
      return 'copied';
    }
    return 'shared';
  } catch (error) {
    if (error?.name === 'AbortError' || /^share cancel(?:l)?ed\.?$/i.test(error?.message || '')) {
      telemetry.finish('cancelled');
      return 'cancelled';
    }
    telemetry.finish('failed');
    throw error;
  }
}
